import { Controller } from "@hotwired/stimulus"

const RESPONSE_FIELD_NAME = "cf-turnstile-response"
const SUCCESS_EVENT = "fansite:turnstile-success"
const EXPIRED_EVENT = "fansite:turnstile-expired"
const ERROR_EVENT = "fansite:turnstile-error"
const TOKEN_PROPERTY = "fansiteTurnstileToken"

export default class extends Controller {
  static targets = ["submit"]

  connect() {
    this.pendingSubmit = false
    this.submittingAfterTurnstile = false
    this.originalSubmitLabel = this.submitLabel
    this.successHandler = (event) => this.turnstileSucceeded(event)
    this.expiredHandler = () => this.turnstileStopped({ clearToken: true })
    this.errorHandler = () => this.turnstileStopped({ clearToken: true })

    window.addEventListener(SUCCESS_EVENT, this.successHandler)
    window.addEventListener(EXPIRED_EVENT, this.expiredHandler)
    window.addEventListener(ERROR_EVENT, this.errorHandler)

    this.installGlobalCallbacks()
    this.hydrateResponseField()
  }

  installGlobalCallbacks() {
    window.fansiteTurnstileSuccess = (token) => {
      window[TOKEN_PROPERTY] = token?.toString() || ""
      window.dispatchEvent(new CustomEvent(SUCCESS_EVENT, { detail: { token: window[TOKEN_PROPERTY] } }))
    }
    window.fansiteTurnstileExpired = () => {
      window[TOKEN_PROPERTY] = ""
      window.dispatchEvent(new CustomEvent(EXPIRED_EVENT))
    }
    window.fansiteTurnstileError = () => {
      window[TOKEN_PROPERTY] = ""
      window.dispatchEvent(new CustomEvent(ERROR_EVENT))
    }
  }

  disconnect() {
    window.removeEventListener(SUCCESS_EVENT, this.successHandler)
    window.removeEventListener(EXPIRED_EVENT, this.expiredHandler)
    window.removeEventListener(ERROR_EVENT, this.errorHandler)
    this.clearWaitingTimeout()
  }

  submit(event) {
    if (this.submittingAfterTurnstile) {
      this.submittingAfterTurnstile = false
      return
    }

    this.hydrateResponseField()

    if (!this.hasTurnstileWidget || this.responseToken) {
      return
    }

    event.preventDefault()
    this.pendingSubmit = true
    this.setWaitingState()
  }

  turnstileSucceeded(event) {
    const token = event.detail?.token?.toString()
    if (token) {
      this.ensureResponseField(token)
    }

    if (!this.pendingSubmit) {
      return
    }

    this.pendingSubmit = false
    this.clearWaitingTimeout()
    this.submittingAfterTurnstile = true
    this.element.requestSubmit()
  }

  turnstileStopped({ clearToken = false } = {}) {
    this.pendingSubmit = false
    if (clearToken) {
      this.clearResponseField()
    }
    this.restoreSubmitState()
    this.clearWaitingTimeout()
  }

  setWaitingState() {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitLabel = "Checking security..."
    }

    this.clearWaitingTimeout()
    this.waitingTimeout = window.setTimeout(() => {
      this.turnstileStopped()
    }, 30000)
  }

  restoreSubmitState() {
    if (!this.hasSubmitTarget) {
      return
    }

    this.submitTarget.disabled = false
    this.submitLabel = this.originalSubmitLabel
  }

  clearWaitingTimeout() {
    if (!this.waitingTimeout) {
      return
    }

    window.clearTimeout(this.waitingTimeout)
    this.waitingTimeout = null
  }

  ensureResponseField(token) {
    let field = this.responseField

    if (!field) {
      field = document.createElement("input")
      field.type = "hidden"
      field.name = RESPONSE_FIELD_NAME
      this.element.appendChild(field)
    }

    field.value = token
  }

  hydrateResponseField() {
    const token = this.responseToken || this.storedResponseToken || this.documentResponseToken
    if (token) {
      this.ensureResponseField(token)
    }
  }

  clearResponseField() {
    const field = this.responseField
    if (field) {
      field.value = ""
    }
  }

  get hasTurnstileWidget() {
    return Boolean(this.element.querySelector(".cf-turnstile"))
  }

  get responseToken() {
    return this.responseField?.value?.trim()
  }

  get responseField() {
    return this.element.querySelector(`[name="${RESPONSE_FIELD_NAME}"]`)
  }

  get storedResponseToken() {
    return window[TOKEN_PROPERTY]?.toString().trim()
  }

  get documentResponseToken() {
    if (typeof document.querySelector !== "function") {
      return ""
    }

    return document.querySelector(`[name="${RESPONSE_FIELD_NAME}"]`)?.value?.trim()
  }

  get submitLabel() {
    if (!this.hasSubmitTarget) {
      return ""
    }

    if ("value" in this.submitTarget) {
      return this.submitTarget.value
    }

    return this.submitTarget.textContent
  }

  set submitLabel(label) {
    if (!this.hasSubmitTarget) {
      return
    }

    if ("value" in this.submitTarget) {
      this.submitTarget.value = label
    } else {
      this.submitTarget.textContent = label
    }
  }
}
